import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import useStore from "../../../store/useStore";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { FaSpinner } from "react-icons/fa";
import { openCashfreeSubscription } from "../../../utils/cashfree";

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

// Fallback static lists used before /config loads or if it fails
const DEFAULT_FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "alternate_day", label: "Alternate Day" },
  { value: "weekly", label: "Weekly" },
];

const PLAN_TYPES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DEFAULT_DELIVERY_SLOTS = [
  { value: "06:00-08:00", label: "6 AM - 8 AM" },
  { value: "08:00-10:00", label: "8 AM - 10 AM" },
];

const DEFAULT_PAYMENT_METHODS = [
  { value: "monthly_billing", label: "Monthly Billing" },
  { value: "wallet", label: "Wallet" },
  { value: "upi_autopay", label: "UPI Autopay" },
];

// Map config array shapes → { value, label } pairs for selects
const mapFrequencies = (freqs) =>
  freqs.length
    ? freqs.map((f) => ({ value: f.freq_key, label: f.label }))
    : DEFAULT_FREQUENCIES;

const mapDeliverySlots = (slots) =>
  slots.length
    ? slots.map((s) => ({ value: s.slot_key, label: s.label }))
    : DEFAULT_DELIVERY_SLOTS;

const mapPaymentMethods = (methods) =>
  methods.length
    ? methods.map((m) => ({ value: m.pm_key, label: m.label }))
    : DEFAULT_PAYMENT_METHODS;

const PAUSE_REASONS = [
  { value: "out_of_station", label: "Out of Station" },
  { value: "vacation", label: "Vacation" },
  { value: "temporary_pause", label: "Temporary Pause" },
  { value: "financial_reason", label: "Financial Reason" },
  { value: "other", label: "Other" },
];

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const tomorrowIso = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

const price = (value) => `Rs ${Number(value || 0).toFixed(2)}`;

const labelFrom = (options, value) =>
  options.find((option) => option.value === value)?.label || value || "Not set";

const getAddressId = (address) =>
  address?.address_id ?? address?.id ?? address?.customer_address_id;

const StatusPill = ({ status }) => {
  const normalized = String(status || "active").toLowerCase();
  const styles = {
    active: "bg-emerald-50 text-emerald-700",
    paused: "bg-amber-50 text-amber-700",
    completed: "bg-blue-50 text-blue-700",
    cancelled: "bg-red-50 text-red-600",
    // UPI AutoPay — waiting for mandate authorisation
    pending_mandate: "bg-indigo-50 text-indigo-700",
  };

  const displayLabel =
    normalized === "pending_mandate" ? "Pending Mandate" : normalized;

  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${
        styles[normalized] || "bg-gray-100 text-gray-600"
      }`}
    >
      {displayLabel}
    </span>
  );
};

const ModalShell = ({ title, children, footer, onClose }) => (
  <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-0 backdrop-blur-sm sm:items-center sm:p-4">
    <div className="flex max-h-[92dvh] w-full max-w-3xl flex-col rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-5 py-4 sm:px-6">
        <h2 className="text-base font-extrabold text-gray-900 sm:text-lg">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition hover:bg-gray-200"
          aria-label="Close"
        >
          <CloseOutlinedIcon style={{ fontSize: 18 }} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 sm:p-6">{children}</div>
      {footer && (
        <div className="sticky bottom-0 border-t border-gray-100 bg-white p-4 sm:px-6">
          {footer}
        </div>
      )}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500">
      {label}
    </span>
    {children}
  </label>
);

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-gray-800 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

const SelectField = ({ value, onChange, children, required = true }) => (
  <select
    value={value}
    onChange={onChange}
    required={required}
    className={inputClass}
  >
    {children}
  </select>
);

const StartSubscriptionModal = ({
  products,
  addresses,
  frequencies,
  deliverySlots,
  paymentMethods,
  initialProductId,
  initialVariantId,
  loading,
  onSubmit,
  onClose,
}) => {
  const [form, setForm] = useState({
    product_id: initialProductId || "",
    product_variant_id: initialVariantId || "",
    address_id: "",
    quantity: 1,
    quantity_unit: "litre",
    frequency: "daily",
    plan_type: "monthly",
    delivery_slot: "06:00-08:00",
    payment_method: "monthly_billing",
    start_date: todayIso(),
  });

  const selectedProduct = useMemo(
    () =>
      products.find(
        (product) => String(product.product_id) === String(form.product_id),
      ),
    [products, form.product_id],
  );

  const selectedVariant = useMemo(
    () =>
      selectedProduct?.variants?.find(
        (variant) =>
          String(variant.product_variant_id) ===
          String(form.product_variant_id),
      ),
    [selectedProduct, form.product_variant_id],
  );

  useEffect(() => {
    const selectedProductExists = products.some(
      (product) => String(product.product_id) === String(form.product_id),
    );
    if (products.length > 0 && (!form.product_id || !selectedProductExists)) {
      setForm((current) => ({
        ...current,
        product_id: products[0].product_id,
      }));
    }
  }, [products, form.product_id]);

  useEffect(() => {
    if (!selectedProduct?.variants?.length) return;
    const hasVariant = selectedProduct.variants.some(
      (variant) =>
        String(variant.product_variant_id) ===
        String(form.product_variant_id),
    );
    if (!hasVariant) {
      setForm((current) => ({
        ...current,
        product_variant_id: selectedProduct.variants[0].product_variant_id,
      }));
    }
  }, [selectedProduct, form.product_variant_id]);

  useEffect(() => {
    if (!form.address_id && addresses.length > 0) {
      const defaultAddress =
        addresses.find((address) => address.is_default) || addresses[0];
      setForm((current) => ({
        ...current,
        address_id: getAddressId(defaultAddress),
      }));
    }
  }, [addresses, form.address_id]);

  const update = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedProduct || !selectedVariant) {
      toast.error("Select a subscription product and size");
      return;
    }
    if (!form.address_id) {
      toast.error("Add a delivery address first");
      return;
    }
    if (Number(form.quantity) <= 0) {
      toast.error("Quantity must be greater than zero");
      return;
    }
    onSubmit(form);
  };

  const unitLabel = selectedVariant?.unit_name || "unit";

  return (
    <ModalShell
      title="Start Milk Subscription"
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-bold text-gray-600 transition hover:bg-gray-50 sm:flex-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="start-subscription-form"
            disabled={loading}
            className="rounded-xl bg-primary-600 px-5 py-3 text-sm font-extrabold text-white shadow-md shadow-primary-600/20 transition hover:bg-primary-700 disabled:opacity-60 sm:flex-1"
          >
            {loading ? (
              <FaSpinner className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              "Activate Subscription"
            )}
          </button>
        </div>
      }
    >
      <form id="start-subscription-form" onSubmit={handleSubmit}>
        <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-5">
            <Field label={`Product (${products.length})`}>
              <div className="max-h-72 overflow-y-auto space-y-2 pr-0.5">
                {products.map((product) => {
                  const selected =
                    String(product.product_id) === String(form.product_id);
                  return (
                    <button
                      key={product.product_id}
                      type="button"
                      onClick={() => update("product_id", product.product_id)}
                      className={`flex w-full min-h-[88px] gap-3 rounded-2xl border p-3 text-left transition ${
                        selected
                          ? "border-primary-500 bg-primary-50 shadow-md shadow-primary-100"
                          : "border-gray-200 bg-white hover:border-primary-300"
                      }`}
                    >
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <Inventory2OutlinedIcon className="text-gray-300" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-sm font-extrabold text-gray-900">
                          {product.name}
                        </p>
                        {product.category_name && (
                          <p className="mt-0.5 text-xs font-semibold text-gray-400">
                            {product.category_name}
                          </p>
                        )}
                        {product.variants?.length > 0 && (
                          <p className="mt-1 text-xs font-semibold text-primary-500">
                            {product.variants.length} size{product.variants.length > 1 ? "s" : ""} available
                          </p>
                        )}
                      </div>
                      {selected && (
                        <span className="shrink-0 self-center text-lg font-extrabold text-primary-600">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Size / Variant">
              <div className="flex flex-wrap gap-2">
                {(selectedProduct?.variants || []).map((variant) => {
                  const selected =
                    String(variant.product_variant_id) ===
                    String(form.product_variant_id);
                  return (
                    <button
                      key={variant.product_variant_id}
                      type="button"
                      onClick={() => {
                        update("product_variant_id", variant.product_variant_id);
                        update("quantity_unit", variant.unit_name || "litre");
                      }}
                      className={`rounded-xl border px-4 py-3 text-left transition ${
                        selected
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-gray-200 bg-white text-gray-700 hover:border-primary-300"
                      }`}
                    >
                      <p className="text-sm font-extrabold">
                        {variant.variant_name}
                        {variant.unit_name ? ` ${variant.unit_name}` : ""}
                      </p>
                      <p className="mt-0.5 text-xs font-bold">
                        {price(variant.discount_price)}
                        {Number(variant.original_price) >
                          Number(variant.discount_price) && (
                          <span className="ml-2 text-gray-400 line-through">
                            {price(variant.original_price)}
                          </span>
                        )}
                      </p>
                    </button>
                  );
                })}
              </div>
            </Field>

            {selectedVariant && (() => {
              const days = form.plan_type === "weekly" ? 7 : 30;
              const estDeliveries =
                form.frequency === "daily"
                  ? days
                  : form.frequency === "alternate_day"
                  ? Math.round(days / 2)
                  : form.frequency === "weekly"
                  ? Math.max(1, Math.floor(days / 7))
                  : days;
              const unitPrice = Number(selectedVariant.discount_price || 0);
              const qty = Number(form.quantity || 1);
              const estTotal = unitPrice * qty * estDeliveries;
              return (
                <div className="rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-50 to-blue-50 p-4">
                  <p className="mb-2.5 text-xs font-bold uppercase tracking-wide text-primary-700">
                    Estimated Plan Cost
                  </p>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between text-gray-600">
                      <span>Unit price</span>
                      <span className="font-bold text-gray-800">
                        {price(selectedVariant.discount_price)} / {unitLabel}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Qty per delivery</span>
                      <span className="font-bold text-gray-800">
                        {form.quantity} {unitLabel}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>
                        Est. deliveries ({labelFrom(PLAN_TYPES, form.plan_type)})
                      </span>
                      <span className="font-bold text-gray-800">
                        ~{estDeliveries}
                      </span>
                    </div>
                    <div className="mt-1 flex justify-between border-t border-primary-200/70 pt-2">
                      <span className="font-bold text-gray-800">Est. Plan Total</span>
                      <span className="font-extrabold text-primary-700">
                        {price(estTotal)}
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 text-[10px] text-gray-400">
                    * Estimate only. Actual amount billed based on deliveries made.
                  </p>
                </div>
              );
            })()}
          </div>

          <div className="space-y-4">
            <Field label="Delivery Address">
              {addresses.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 py-5 text-center text-xs font-semibold text-gray-400">
                  No saved addresses. Add one from My Addresses.
                </div>
              ) : (
                <div className="max-h-48 space-y-2 overflow-y-auto pr-0.5">
                  {addresses.map((address) => {
                    const addrId = getAddressId(address);
                    const isSelected =
                      String(form.address_id) === String(addrId);
                    return (
                      <button
                        key={addrId}
                        type="button"
                        onClick={() => update("address_id", addrId)}
                        className={`w-full rounded-xl border p-3 text-left transition ${
                          isSelected
                            ? "border-primary-500 bg-primary-50 shadow-sm"
                            : "border-gray-200 bg-white hover:border-primary-300"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-extrabold text-gray-800">
                              {address.address_type || "Home"}
                              {address.is_default && (
                                <span className="ml-1.5 text-[10px] font-bold text-primary-500">
                                  ★ Default
                                </span>
                              )}
                            </p>
                            <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                              {[address.address, address.city, address.pincode]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          </div>
                          {isSelected && (
                            <span className="shrink-0 text-sm font-extrabold text-primary-600">
                              ✓
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Field>

            <Field label={`Quantity (${unitLabel})`}>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={form.quantity}
                onChange={(event) => update("quantity", event.target.value)}
                className={inputClass}
                required
              />
            </Field>

            <Field label="Frequency">
              <SelectField
                value={form.frequency}
                onChange={(event) => update("frequency", event.target.value)}
              >
                {frequencies.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectField>
            </Field>

            <Field label="Plan Type">
              <SelectField
                value={form.plan_type}
                onChange={(event) => update("plan_type", event.target.value)}
              >
                {PLAN_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} ({option.value === "weekly" ? "7 days" : "30 days"})
                  </option>
                ))}
              </SelectField>
            </Field>

            <Field label="Delivery Slot">
              <SelectField
                value={form.delivery_slot}
                onChange={(event) => update("delivery_slot", event.target.value)}
              >
                {deliverySlots.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectField>
            </Field>

            {/* Payment Method + contextual info banner */}
            <div>
              <Field label="Payment Method">
                <SelectField
                  value={form.payment_method}
                  onChange={(event) =>
                    update("payment_method", event.target.value)
                  }
                >
                  {paymentMethods.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectField>
              </Field>
              {form.payment_method === "monthly_billing" && (
                <div className="mt-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs font-semibold text-blue-800">
                  <span className="font-extrabold">Monthly Billing: </span>
                  You'll receive a consolidated bill at the end of each delivery
                  month. Pay via UPI, card, or cash.
                </div>
              )}
              {form.payment_method === "wallet" && (
                <div className="mt-2 rounded-xl border border-violet-100 bg-violet-50 px-3 py-2.5 text-xs font-semibold text-violet-800">
                  <span className="font-extrabold">Wallet: </span>
                  Amount is deducted from your KisanFresh Wallet before each
                  delivery. Please ensure sufficient wallet balance to avoid
                  missed deliveries.
                </div>
              )}
              {form.payment_method === "upi_autopay" && (
                <div className="mt-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-xs font-semibold text-emerald-800">
                  <span className="font-extrabold">UPI AutoPay: </span>
                  After activation, you'll receive a UPI mandate request in your
                  UPI app. Approve it to enable automatic daily deductions — no
                  manual payment needed each day.
                </div>
              )}
            </div>

            {/* Start Date — minimum = today */}
            <Field label="Start Date">
              <input
                type="date"
                min={todayIso()}
                value={form.start_date}
                onChange={(event) => update("start_date", event.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        </div>
      </form>
    </ModalShell>
  );
};

const PauseModal = ({ subscription, loading, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    pause_from: tomorrowIso(),
    pause_to: tomorrowIso(),
    reason: "temporary_pause",
    comments: "",
  });

  const update = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (form.pause_to < form.pause_from) {
      toast.error("Pause end date must be after start date");
      return;
    }
    onSubmit(form);
  };

  return (
    <ModalShell
      title={`Pause ${subscription.product_name || "Subscription"}`}
      onClose={onClose}
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 py-3 text-sm font-bold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="pause-subscription-form"
            disabled={loading}
            className="flex-1 rounded-xl bg-amber-500 py-3 text-sm font-extrabold text-white hover:bg-amber-600 disabled:opacity-60"
          >
            {loading ? (
              <FaSpinner className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              "Pause"
            )}
          </button>
        </div>
      }
    >
      <form
        id="pause-subscription-form"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Pause From">
            <input
              type="date"
              min={tomorrowIso()}
              value={form.pause_from}
              onChange={(event) => update("pause_from", event.target.value)}
              className={inputClass}
              required
            />
          </Field>
          <Field label="Pause To">
            <input
              type="date"
              min={form.pause_from || tomorrowIso()}
              value={form.pause_to}
              onChange={(event) => update("pause_to", event.target.value)}
              className={inputClass}
              required
            />
          </Field>
        </div>
        <Field label="Reason">
          <SelectField
            value={form.reason}
            onChange={(event) => update("reason", event.target.value)}
          >
            {PAUSE_REASONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field label="Comments">
          <textarea
            rows={3}
            maxLength={200}
            value={form.comments}
            onChange={(event) => update("comments", event.target.value)}
            className={inputClass}
            placeholder="Optional note"
          />
        </Field>
      </form>
    </ModalShell>
  );
};

const ResumeModal = ({ subscription, loading, onSubmit, onClose }) => {
  const [resumeDate, setResumeDate] = useState(todayIso());

  return (
    <ModalShell
      title={`Resume ${subscription.product_name || "Subscription"}`}
      onClose={onClose}
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 py-3 text-sm font-bold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(resumeDate)}
            disabled={loading}
            className="flex-1 rounded-xl bg-primary-600 py-3 text-sm font-extrabold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {loading ? (
              <FaSpinner className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              "Resume Now"
            )}
          </button>
        </div>
      }
    >
      <Field label="Resume Date">
        <input
          type="date"
          value={resumeDate}
          onChange={(event) => setResumeDate(event.target.value)}
          className={inputClass}
        />
      </Field>
    </ModalShell>
  );
};

const EditSubscriptionModal = ({ subscription, loading, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    quantity: Number(subscription.quantity || 1),
    quantity_unit: subscription.quantity_unit || "litre",
    frequency: subscription.frequency || "daily",
    plan_type: subscription.plan_type || "monthly",
    delivery_slot: subscription.delivery_slot || "06:00-08:00",
  });

  const update = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (Number(form.quantity) <= 0) {
      toast.error("Quantity must be greater than zero");
      return;
    }
    onSubmit(form);
  };

  return (
    <ModalShell
      title="Change Quantity / Plan"
      onClose={onClose}
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 py-3 text-sm font-bold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-subscription-form"
            disabled={loading}
            className="flex-1 rounded-xl bg-primary-600 py-3 text-sm font-extrabold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {loading ? (
              <FaSpinner className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      }
    >
      <form
        id="edit-subscription-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <Field label="Quantity">
          <input
            type="number"
            min="0.1"
            step="0.1"
            value={form.quantity}
            onChange={(event) => update("quantity", event.target.value)}
            className={inputClass}
            required
          />
        </Field>
        <Field label="Unit">
          <SelectField
            value={form.quantity_unit}
            onChange={(event) => update("quantity_unit", event.target.value)}
          >
            <option value="litre">Litre</option>
            <option value="ml">ml</option>
            <option value="kg">kg</option>
          </SelectField>
        </Field>
        <Field label="Frequency">
          <SelectField
            value={form.frequency}
            onChange={(event) => update("frequency", event.target.value)}
          >
            {DEFAULT_FREQUENCIES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field label="Plan Type">
          <SelectField
            value={form.plan_type}
            onChange={(event) => update("plan_type", event.target.value)}
          >
            {PLAN_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Delivery Slot">
            <SelectField
              value={form.delivery_slot}
              onChange={(event) => update("delivery_slot", event.target.value)}
            >
              {DEFAULT_DELIVERY_SLOTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </Field>
        </div>
      </form>
    </ModalShell>
  );
};

const DetailModal = ({
  detail,
  deliveries,
  detailLoading,
  deliveriesLoading,
  onLoadDeliveries,
  onClose,
}) => {
  const [view, setView] = useState("details");

  useEffect(() => {
    if (view === "deliveries" && detail?.subscription_id) {
      onLoadDeliveries(detail.subscription_id, { page: deliveries.page || 1 });
    }
  }, [view, detail?.subscription_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ModalShell title="Subscription Detail" onClose={onClose}>
      <div className="mb-5 flex gap-2">
        {["details", "deliveries"].map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setView(item)}
            className={`rounded-xl px-4 py-2 text-sm font-bold capitalize ${
              view === item
                ? "bg-primary-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {item}
          </button>
        ))}
      </div>

      {detailLoading && (
        <div className="flex justify-center py-12">
          <FaSpinner className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      )}

      {!detailLoading && detail && view === "details" && (
        <div className="space-y-5">
          <div className="flex items-start gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">
              {detail.image1 ? (
                <img
                  src={detail.image1}
                  alt={detail.product_name}
                  className="h-full w-full object-contain"
                />
              ) : (
                <Inventory2OutlinedIcon className="text-gray-300" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-extrabold text-gray-900">
                  {detail.product_name}
                </h3>
                <StatusPill status={detail.status} />
              </div>
              <p className="text-sm font-semibold text-gray-500">
                {detail.variant_name} - {detail.quantity} {detail.quantity_unit}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              ["Plan", labelFrom(PLAN_TYPES, detail.plan_type)],
              ["Frequency", labelFrom(DEFAULT_FREQUENCIES, detail.frequency)],
              ["Slot", labelFrom(DEFAULT_DELIVERY_SLOTS, detail.delivery_slot)],
              ["Payment", labelFrom(DEFAULT_PAYMENT_METHODS, detail.payment_method)],
              ["Start", formatDate(detail.start_date)],
              ["End", formatDate(detail.end_date)],
              ["Next Delivery", formatDate(detail.next_delivery)],
              ["Unit Price", price(detail.unit_price)],
              ["Plan Amount", price(detail.total_plan_amount)],
              ["Delivered", detail.delivered_count ?? 0],
              ["Pending", detail.pending_count ?? 0],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
                  {label}
                </p>
                <p className="mt-1 text-sm font-extrabold text-gray-900">
                  {value || "Not set"}
                </p>
              </div>
            ))}
          </div>

          {(detail.address || detail.city || detail.pincode) && (
            <div className="rounded-xl bg-primary-50 px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wide text-primary-600">
                Delivery Address
              </p>
              <p className="mt-1 text-sm font-bold text-gray-800">
                {[detail.address, detail.city, detail.state, detail.pincode]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
          )}

          {detail.active_pause && (
            <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-1">Active Pause</p>
              <p className="text-sm font-bold text-amber-900">
                {formatDate(detail.active_pause.pause_from)} → {formatDate(detail.active_pause.pause_to)}
              </p>
              {detail.active_pause.reason && (
                <p className="text-xs font-semibold text-amber-700 mt-0.5">
                  {String(detail.active_pause.reason).replace(/_/g, " ")}
                  {detail.active_pause.comments ? ` — ${detail.active_pause.comments}` : ""}
                </p>
              )}
            </div>
          )}

          {detail.pause_history?.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-extrabold text-gray-900">
                Pause History
              </h3>
              <div className="space-y-2">
                {detail.pause_history.map((pause) => (
                  <div
                    key={pause.pause_id || `${pause.pause_from}-${pause.pause_to}`}
                    className="rounded-xl border border-gray-100 px-4 py-3 text-sm"
                  >
                    <p className="font-bold text-gray-800">
                      {formatDate(pause.pause_from)} to{" "}
                      {formatDate(pause.pause_to)}
                    </p>
                    <p className="text-gray-500">
                      {String(pause.reason || "Pause").replace(/_/g, " ")}{pause.comments ? ` — ${pause.comments}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {view === "deliveries" && (
        <div>
          {deliveriesLoading && (
            <div className="flex justify-center py-12">
              <FaSpinner className="h-6 w-6 animate-spin text-primary-600" />
            </div>
          )}

          {!deliveriesLoading && deliveries.deliveries.length === 0 && (
            <div className="rounded-2xl border border-dashed border-gray-200 py-12 text-center">
              <LocalShippingOutlinedIcon
                className="text-gray-300"
                style={{ fontSize: 40 }}
              />
              <p className="mt-2 text-sm font-bold text-gray-500">
                No deliveries found
              </p>
            </div>
          )}

          {!deliveriesLoading && deliveries.deliveries.length > 0 && (
            <div className="space-y-3">
              {deliveries.deliveries.map((delivery) => (
                <div
                  key={delivery.schedule_id || delivery.scheduled_date}
                  className="rounded-2xl border border-gray-100 px-4 py-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-extrabold text-gray-900">
                        {formatDate(delivery.scheduled_date)}
                      </p>
                      {delivery.order_number && (
                        <p className="text-xs font-semibold text-gray-400">
                          {delivery.order_number}
                        </p>
                      )}
                    </div>
                    {(() => {
                      const ds = String(delivery.status || "pending");
                      const dsLabel = ds.replace(/_/g, " ");
                      const dsClass =
                        ds === "order_created"
                          ? "bg-emerald-50 text-emerald-700"
                          : ds === "pending"
                          ? "bg-blue-50 text-blue-700"
                          : ds === "skipped"
                          ? "bg-amber-50 text-amber-700"
                          : ds === "failed"
                          ? "bg-red-50 text-red-600"
                          : "bg-gray-100 text-gray-600";
                      return (
                        <span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${dsClass}`}>
                          {dsLabel}
                        </span>
                      );
                    })()}
                  </div>
                  {(delivery.payment_status || delivery.total_amount) && (
                    <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold text-gray-500">
                      {delivery.order_status && <span>{delivery.order_status}</span>}
                      {delivery.payment_status && (
                        <span>{delivery.payment_status}</span>
                      )}
                      {delivery.total_amount && (
                        <span>{price(delivery.total_amount)}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {deliveries.total > deliveries.limit && (
                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    disabled={deliveries.page <= 1}
                    onClick={() =>
                      onLoadDeliveries(detail.subscription_id, {
                        page: deliveries.page - 1,
                      })
                    }
                    className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-600 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-xs font-bold text-gray-400">
                    Page {deliveries.page}
                  </span>
                  <button
                    type="button"
                    disabled={deliveries.page * deliveries.limit >= deliveries.total}
                    onClick={() =>
                      onLoadDeliveries(detail.subscription_id, {
                        page: deliveries.page + 1,
                      })
                    }
                    className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-600 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </ModalShell>
  );
};

const SubscriptionCard = ({
  subscription,
  onDetail,
  onPause,
  onResume,
  onEdit,
  onCancel,
  actionLoading,
}) => {
  const status = String(subscription.status || "").toLowerCase();
  const canPause = status === "active";
  const canResume = status === "paused";
  const canEdit = ["active", "paused"].includes(status);
  const canCancel = !["completed", "cancelled"].includes(status);

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-md shadow-black/5 transition hover:shadow-lg sm:p-5">
      <div className="flex gap-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gray-50">
          {subscription.image1 ? (
            <img
              src={subscription.image1}
              alt={subscription.product_name}
              className="h-full w-full object-contain"
            />
          ) : (
            <Inventory2OutlinedIcon className="text-gray-300" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="line-clamp-2 text-base font-extrabold text-gray-900">
                {subscription.product_name || "Milk Subscription"}
              </h3>
              <p className="mt-0.5 text-xs font-semibold text-gray-400">
                {subscription.variant_name || subscription.unit_name || "Selected size"}
              </p>
            </div>
            <StatusPill status={subscription.status} />
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <div className="rounded-xl bg-gray-50 px-3 py-2">
              <p className="font-bold text-gray-400">Qty</p>
              <p className="font-extrabold text-gray-800">
                {subscription.quantity} {subscription.quantity_unit}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-3 py-2">
              <p className="font-bold text-gray-400">Plan</p>
              <p className="font-extrabold text-gray-800">
                {labelFrom(PLAN_TYPES, subscription.plan_type)}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-3 py-2">
              <p className="font-bold text-gray-400">Next</p>
              <p className="font-extrabold text-gray-800">
                {formatDate(subscription.next_delivery)}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-3 py-2">
              <p className="font-bold text-gray-400">Amount</p>
              <p className="font-extrabold text-gray-800">
                {price(subscription.total_plan_amount)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {subscription.active_pause && (
        <div className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-bold">
            Paused from {formatDate(subscription.active_pause.pause_from)} to{" "}
            {formatDate(subscription.active_pause.pause_to)}
          </p>
          {subscription.active_pause.reason && (
            <p className="text-xs font-semibold">
              {String(subscription.active_pause.reason).replace(/_/g, " ")}
            </p>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onDetail(subscription)}
          className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 transition hover:bg-gray-50"
        >
          <VisibilityOutlinedIcon style={{ fontSize: 15 }} /> Details
        </button>
        {canEdit && (
          <button
            type="button"
            onClick={() => onEdit(subscription)}
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 transition hover:bg-gray-50"
          >
            <EditOutlinedIcon style={{ fontSize: 15 }} /> Edit
          </button>
        )}
        {canPause && (
          <button
            type="button"
            onClick={() => onPause(subscription)}
            className="flex items-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 transition hover:bg-amber-100"
          >
            <PauseCircleOutlineOutlinedIcon style={{ fontSize: 15 }} /> Pause
          </button>
        )}
        {canResume && (
          <button
            type="button"
            onClick={() => onResume(subscription)}
            className="flex items-center gap-1.5 rounded-xl bg-primary-50 px-3 py-2 text-xs font-bold text-primary-700 transition hover:bg-primary-100"
          >
            <PlayCircleOutlineOutlinedIcon style={{ fontSize: 15 }} /> Resume
          </button>
        )}
        {canCancel && (
          <button
            type="button"
            disabled={actionLoading}
            onClick={() => onCancel(subscription)}
            className="ml-auto flex items-center gap-1.5 rounded-xl bg-red-50 px-3 py-2 text-xs font-bold text-red-600 transition hover:bg-red-100 disabled:opacity-50"
          >
            <DeleteOutlineOutlinedIcon style={{ fontSize: 15 }} /> Cancel
          </button>
        )}
      </div>
    </div>
  );
};

const ProductShowcaseCard = ({ product, onSubscribe }) => {
  const [selectedVariant, setSelectedVariant] = useState(
    product.variants?.[0] || null,
  );

  return (
    <div className="flex flex-col rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md overflow-hidden">
      {/* Product image */}
      <div className="flex h-44 items-center justify-center bg-gradient-to-br from-primary-50 to-blue-50 p-4">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="h-full w-full object-contain drop-shadow-sm"
          />
        ) : (
          <Inventory2OutlinedIcon
            className="text-primary-200"
            style={{ fontSize: 56 }}
          />
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="mb-3 text-sm font-extrabold leading-snug text-gray-900">
          {product.name}
        </h3>

        {/* Variant chips */}
        {product.variants?.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {product.variants.map((variant) => {
              const isSelected =
                String(variant.product_variant_id) ===
                String(selectedVariant?.product_variant_id);
              return (
                <button
                  key={variant.product_variant_id}
                  type="button"
                  onClick={() => setSelectedVariant(variant)}
                  className={`rounded-xl border px-3 py-2 text-left transition ${
                    isSelected
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-gray-200 bg-gray-50 text-gray-600 hover:border-primary-300"
                  }`}
                >
                  <p className="text-xs font-extrabold">{variant.variant_name}</p>
                  <p className="mt-0.5 text-[11px] font-bold">
                    Rs {Number(variant.discount_price || 0).toFixed(2)}
                    {Number(variant.original_price) > Number(variant.discount_price) && (
                      <span className="ml-1.5 font-semibold text-gray-400 line-through">
                        Rs {Number(variant.original_price).toFixed(2)}
                      </span>
                    )}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        {/* Price summary for selected variant */}
        {selectedVariant && (
          <div className="mb-4 rounded-xl bg-gray-50 px-3 py-2.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-500">Per delivery</span>
              <span className="font-extrabold text-gray-900">
                Rs {Number(selectedVariant.discount_price || 0).toFixed(2)}
                {" / "}{selectedVariant.unit_name || "unit"}
              </span>
            </div>
            {Number(selectedVariant.original_price) > Number(selectedVariant.discount_price) && (
              <div className="mt-1 flex items-center justify-between">
                <span className="font-semibold text-emerald-600">You save</span>
                <span className="font-extrabold text-emerald-600">
                  Rs {(Number(selectedVariant.original_price) - Number(selectedVariant.discount_price)).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() =>
            onSubscribe(product.product_id, selectedVariant?.product_variant_id)
          }
          className="mt-auto flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 py-2.5 text-sm font-extrabold text-white shadow-sm shadow-primary-600/20 transition hover:bg-primary-700 active:scale-95"
        >
          <AddOutlinedIcon style={{ fontSize: 16 }} />
          Subscribe
        </button>
      </div>
    </div>
  );
};

// ── Mandate Result Screen ────────────────────────────────────────────────────
// Full-page overlay rendered AFTER the Cashfree mandate flow completes.
// status: 'checking' | 'active' | 'pending_mandate' | 'failed'
const MandateResultScreen = ({
  status,
  subscription,
  paymentLink,
  onViewSubscriptions,
  onCheckStatus,
  onRetry,
  isPolling,
}) => {
  const isChecking = status === "checking";
  const isActive   = status === "active";
  const isPending  = status === "pending_mandate";
  // Everything else (cancelled, failed, etc.) → failure

  return (
    <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-white px-4">
      <div className="w-full max-w-md text-center">

        {/* ── Checking / Polling ── */}
        {isChecking && (
          <>
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-indigo-50">
              <FaSpinner className="h-10 w-10 animate-spin text-indigo-600" />
            </div>
            <h1 className="mb-2 text-2xl font-extrabold text-gray-900">Verifying Mandate…</h1>
            <p className="text-sm text-gray-500">
              Please wait while we confirm your UPI AutoPay mandate.
            </p>
            <div className="mt-6 flex justify-center gap-1.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-2 w-2 rounded-full bg-indigo-400"
                  style={{ animation: `pulse 1.2s ease-in-out ${i * 0.4}s infinite`, opacity: 0 }}
                />
              ))}
            </div>
          </>
        )}

        {/* ── Success / Active ── */}
        {isActive && (
          <>
            <div
              className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full shadow-xl"
              style={{ background: "linear-gradient(135deg, #16a34a, #15803d)", boxShadow: "0 20px 40px rgba(22,163,74,0.35)" }}
            >
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="mb-2 text-2xl font-extrabold text-gray-900">Mandate Approved! 🎉</h1>
            <p className="mb-1 text-gray-500">
              Your UPI AutoPay mandate has been authorised.
            </p>
            {subscription && (
              <p className="mb-1 text-sm font-semibold text-gray-700">
                {subscription.product_name} — {subscription.quantity} {subscription.quantity_unit} / {subscription.frequency}
              </p>
            )}
            <p className="mb-8 text-sm text-gray-400">
              Daily deliveries start from{" "}
              <span className="font-bold text-gray-700">
                {subscription?.next_delivery
                  ? new Date(subscription.next_delivery).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                  : "tomorrow"}
              </span>.
            </p>
            <button
              type="button"
              onClick={onViewSubscriptions}
              className="w-full rounded-2xl bg-primary-600 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-primary-600/30 hover:bg-primary-700"
            >
              View My Subscriptions
            </button>
          </>
        )}

        {/* ── Pending / Under Review ── */}
        {isPending && (
          <>
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-amber-50">
              <svg className="h-12 w-12 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="mb-2 text-2xl font-extrabold text-gray-900">Authorize Your Mandate ⏳</h1>
            {paymentLink ? (
              <p className="mb-2 text-gray-500">
                A payment page has opened in a new tab. Complete your UPI mandate there, then tap{" "}
                <strong>Check Status</strong> below.
              </p>
            ) : (
              <p className="mb-2 text-gray-500">
                A UPI mandate request has been sent. Please open your UPI app and approve it, then tap{" "}
                <strong>Check Status</strong> below.
              </p>
            )}
            <p className="mb-6 text-sm text-gray-400">
              Your subscription activates automatically once approved.
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={onCheckStatus}
                disabled={isPolling}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-600 py-3.5 text-sm font-extrabold text-white shadow-lg hover:bg-primary-700 disabled:opacity-60"
              >
                {isPolling ? (
                  <><FaSpinner className="h-4 w-4 animate-spin" /> Checking…</>
                ) : (
                  "Check Mandate Status"
                )}
              </button>
              {paymentLink && (
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full rounded-2xl border-2 border-primary-200 py-3.5 text-sm font-bold text-primary-600 hover:bg-primary-50 block"
                >
                  Re-open Payment Page ↗
                </a>
              )}
              <button
                type="button"
                onClick={onViewSubscriptions}
                className="w-full rounded-2xl border-2 border-gray-200 py-3.5 text-sm font-bold text-gray-500 hover:bg-gray-50"
              >
                Do This Later
              </button>
            </div>
          </>
        )}

        {/* ── Failed ── */}
        {!isChecking && !isActive && !isPending && (
          <>
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-50">
              <svg className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="mb-2 text-2xl font-extrabold text-gray-900">Mandate Failed</h1>
            <p className="mb-2 text-gray-500">
              The UPI mandate was not approved. Your subscription is not yet active.
            </p>
            <p className="mb-8 text-sm text-gray-400">
              You can try again with a different payment method or retry the UPI mandate.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={onRetry}
                className="flex-1 rounded-2xl bg-primary-600 py-3.5 text-sm font-extrabold text-white shadow-lg hover:bg-primary-700"
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={onViewSubscriptions}
                className="flex-1 rounded-2xl border-2 border-gray-200 py-3.5 text-sm font-bold text-gray-600 hover:bg-gray-50"
              >
                My Subscriptions
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SubscriptionsSection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const [startOpen, setStartOpen] = useState(false);
  const [pauseTarget, setPauseTarget] = useState(null);
  const [resumeTarget, setResumeTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // Track which product/variant was clicked from the showcase to pre-fill the modal
  const [modalProductId, setModalProductId] = useState("");
  const [modalVariantId, setModalVariantId] = useState("");
  // UPI AutoPay mandate flow state
  // null = not running | 'pending_mandate' | 'checking' | 'active' | 'failed' | 'cancelled'
  const [mandateStatus, setMandateStatus] = useState(null);
  const [mandateSubscription, setMandateSubscription] = useState(null);
  const [mandatePaymentLink, setMandatePaymentLink] = useState(null);
  const [mandatePolling, setMandatePolling] = useState(false);

  const {
    subscription_config,
    subscription_config_loading,
    subscription_products,
    subscription_products_loading,
    subscriptions,
    subscriptions_loading,
    subscriptions_error,
    subscription_detail,
    subscription_detail_loading,
    subscription_deliveries,
    subscription_deliveries_loading,
    subscription_action_loading,
    fetchSubscriptionConfig,
    fetchSubscriptions,
    createSubscription,
    verifySubscriptionStatus,
    fetchSubscriptionDetail,
    pauseSubscription,
    resumeSubscription,
    changeSubscriptionQuantity,
    changeSubscriptionPlan,
    fetchSubscriptionDeliveries,
    cancelSubscription,
    user_addresses,
    fetchUserAddresses,
  } = useStore();

  // Derive form option lists from config (falls back to defaults if not yet loaded)
  const configFrequencies = useMemo(
    () => mapFrequencies(subscription_config?.frequencies || []),
    [subscription_config],
  );
  const configDeliverySlots = useMemo(
    () => mapDeliverySlots(subscription_config?.delivery_slots || []),
    [subscription_config],
  );
  const configPaymentMethods = useMemo(
    () => mapPaymentMethods(subscription_config?.payment_methods || []),
    [subscription_config],
  );

  // modalProductId/VariantId (set by showcase cards) take precedence over URL params
  const initialProductId = modalProductId || searchParams.get("productId") || "";
  const initialVariantId = modalVariantId || searchParams.get("variantId") || "";

  useEffect(() => {
    // STEP 0: load the full config (products, plans, frequencies, slots, payment methods)
    fetchSubscriptionConfig();
    fetchUserAddresses();
  }, [fetchSubscriptionConfig, fetchUserAddresses]);

  useEffect(() => {
    fetchSubscriptions(status);
  }, [fetchSubscriptions, status]);

  useEffect(() => {
    if (initialProductId) {
      setStartOpen(true);
    }
  }, [initialProductId]);

  const refreshCurrentTab = () => fetchSubscriptions(status);

  const handleSubscribeFromShowcase = (productId, variantId) => {
    setModalProductId(String(productId || ""));
    setModalVariantId(String(variantId || ""));
    setStartOpen(true);
  };

  const handleCreate = async (form) => {
    try {
      const { subscription, raw } = await createSubscription(form);
      console.log('[UPI AutoPay] raw API response:', JSON.stringify(raw, null, 2));

      // ── UPI AutoPay ────────────────────────────────────────────────────────────
      if (form.payment_method === "upi_autopay") {
        setStartOpen(false);
        setSearchParams({});
        setModalProductId("");
        setModalVariantId("");
        setMandateSubscription(subscription);
        setMandatePaymentLink(raw?.cashfree?.payment_link || null);

        const paymentLink = raw?.cashfree?.payment_link;

        if (paymentLink) {
          // Redirect user to Cashfree mandate authorization page
          const returnUrl = `${window.location.origin}/subscription/mandate/return?sub_id=${subscription.subscription_id}`;
          openCashfreeSubscription({ paymentLink, returnUrl });
          // Page will redirect — show pending screen briefly
          setMandateStatus("pending_mandate");
        } else {
          // No payment link — show pending screen so user can check later
          setMandateStatus("pending_mandate");
        }
        return;
      }

      // ── All other payment methods (wallet / monthly_billing) ─────────────────
      toast.success("Subscription activated successfully");
      setStartOpen(false);
      setSearchParams({});
      await refreshCurrentTab();
      await fetchSubscriptionDetail(subscription.subscription_id);
      setDetailOpen(true);
    } catch (error) {
      toast.error(error.message || "Failed to activate subscription");
    }
  };

  // Manual "Check Status" button in the pending overlay — polls the subscription
  const handleCheckMandateStatus = async () => {
    if (!mandateSubscription?.subscription_id || mandatePolling) return;
    setMandatePolling(true);
    setMandateStatus("checking");
    try {
      const resolvedStatus = await verifySubscriptionStatus(
        mandateSubscription.subscription_id,
      );
      const fresh = await fetchSubscriptionDetail(
        mandateSubscription.subscription_id,
      );
      setMandateSubscription(fresh || mandateSubscription);
      setMandateStatus(resolvedStatus);
      if (resolvedStatus !== "pending_mandate") refreshCurrentTab();
    } catch {
      setMandateStatus("pending_mandate"); // revert to pending on error
    } finally {
      setMandatePolling(false);
    }
  };

  // Dismiss the mandate result overlay and go back to the subscriptions list
  const handleMandateDismiss = () => {
    setMandateStatus(null);
    setMandateSubscription(null);
    setMandatePaymentLink(null);
    refreshCurrentTab();
  };

  // User wants to retry after a failed mandate
  const handleMandateRetry = () => {
    setMandateStatus(null);
    setMandateSubscription(null);
    setMandatePaymentLink(null);
    setStartOpen(true);
  };

  const handlePause = async (form) => {
    try {
      await pauseSubscription(pauseTarget.subscription_id, form);
      toast.success("Subscription paused successfully");
      setPauseTarget(null);
      await refreshCurrentTab();
    } catch (error) {
      toast.error(error.message || "Failed to pause subscription");
    }
  };

  const handleResume = async (resumeDate) => {
    try {
      await resumeSubscription(resumeTarget.subscription_id, resumeDate);
      toast.success("Subscription resumed successfully");
      setResumeTarget(null);
      await refreshCurrentTab();
    } catch (error) {
      toast.error(error.message || "Failed to resume subscription");
    }
  };

  const handleEdit = async (form) => {
    const id = editTarget.subscription_id;
    try {
      // Run both PATCH calls sequentially; if either throws, catch and show error
      await changeSubscriptionQuantity(id, {
        quantity: form.quantity,
        quantity_unit: form.quantity_unit,
      });
      await changeSubscriptionPlan(id, {
        frequency: form.frequency,
        plan_type: form.plan_type,
        delivery_slot: form.delivery_slot,
      });
      toast.success("Subscription updated successfully");
      setEditTarget(null);
      await refreshCurrentTab();
    } catch (error) {
      toast.error(error.message || "Failed to update subscription");
      // Refetch to restore a consistent state after partial failure
      await refreshCurrentTab();
    }
  };

  const handleDetail = async (subscription) => {
    try {
      setDetailOpen(true);
      await fetchSubscriptionDetail(subscription.subscription_id);
    } catch (error) {
      toast.error(error.message || "Failed to load subscription detail");
    }
  };

  const handleCancel = async (subscription) => {
    const confirmed = window.confirm(
      "Cancel this subscription permanently? This cannot be undone.",
    );
    if (!confirmed) return;

    try {
      await cancelSubscription(subscription.subscription_id);
      toast.success("Subscription cancelled");
      // Always refetch so the list reflects the server state (cancelled status)
      await refreshCurrentTab();
      // If detail modal was open for the cancelled subscription, close it
      if (
        detailOpen &&
        String(subscription_detail?.subscription_id) ===
          String(subscription.subscription_id)
      ) {
        setDetailOpen(false);
      }
    } catch (error) {
      toast.error(error.message || "Failed to cancel subscription");
    }
  };

  return (
    <div className="pt-2">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900">
            My Subscriptions
          </h1>
          <p className="mt-0.5 text-sm text-gray-400">
            Manage daily milk plans, pauses, and delivery history
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={refreshCurrentTab}
            disabled={subscriptions_loading}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
          >
            {subscriptions_loading ? (
              <FaSpinner className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshOutlinedIcon style={{ fontSize: 17 }} />
            )}
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setStartOpen(true)}
            className="flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-md shadow-primary-600/20 transition hover:bg-primary-700"
          >
            <AddOutlinedIcon style={{ fontSize: 17 }} />
            Subscribe
          </button>
        </div>
      </div>

      <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            type="button"
            onClick={() => setStatus(tab.value)}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-bold transition ${
              status === tab.value
                ? "bg-primary-600 text-white shadow-md shadow-primary-600/15"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subscriptions_error && (
        <div className="mb-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
          {subscriptions_error}
        </div>
      )}

      {subscriptions_loading && subscriptions.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <FaSpinner className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-sm font-semibold text-gray-400">
            Loading subscriptions
          </p>
        </div>
      )}

      {!subscriptions_loading && subscriptions.length === 0 && (
        <div className="rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50/60 px-5 py-14 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
            <AutorenewOutlinedIcon style={{ fontSize: 34 }} />
          </div>
          <h2 className="text-lg font-extrabold text-gray-900">
            No subscriptions found
          </h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
            Start a weekly or monthly milk subscription and manage delivery
            changes from here.
          </p>
          <button
            type="button"
            onClick={() => setStartOpen(true)}
            className="mt-5 rounded-xl bg-primary-600 px-5 py-3 text-sm font-extrabold text-white hover:bg-primary-700"
          >
            Start Subscription
          </button>
        </div>
      )}

      {subscriptions.length > 0 && (
        <div className="space-y-4">
          {subscriptions.map((subscription) => (
            <SubscriptionCard
              key={subscription.subscription_id}
              subscription={subscription}
              onDetail={handleDetail}
              onPause={setPauseTarget}
              onResume={setResumeTarget}
              onEdit={setEditTarget}
              onCancel={handleCancel}
              actionLoading={subscription_action_loading}
            />
          ))}
        </div>
      )}

      {/* ── Available Milk Products ───────────────────────────────────────── */}
      <div className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-extrabold text-gray-900">
              🥛 Available Milk Products
            </h2>
            <p className="mt-0.5 text-xs font-semibold text-gray-400">
              Pick a product below to start a new subscription
            </p>
          </div>
        </div>

        {subscription_config_loading && subscription_products.length === 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2].map((n) => (
              <div
                key={n}
                className="h-72 animate-pulse rounded-2xl bg-gray-100"
              />
            ))}
          </div>
        )}

        {!subscription_config_loading && subscription_products.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 py-10 text-center">
            <Inventory2OutlinedIcon
              className="text-gray-300"
              style={{ fontSize: 36 }}
            />
            <p className="mt-2 text-sm font-bold text-gray-400">
              No products available right now
            </p>
          </div>
        )}

        {subscription_products.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {subscription_products.map((product) => (
              <ProductShowcaseCard
                key={product.product_id}
                product={product}
                onSubscribe={handleSubscribeFromShowcase}
              />
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
        <div className="rounded-2xl bg-primary-50 px-4 py-3 text-primary-800">
          <CalendarMonthOutlinedIcon style={{ fontSize: 18 }} />
          <p className="mt-1 font-bold">Weekly and monthly plans</p>
        </div>
        <div className="rounded-2xl bg-blue-50 px-4 py-3 text-blue-800">
          <LocalShippingOutlinedIcon style={{ fontSize: 18 }} />
          <p className="mt-1 font-bold">Delivery log from server schedule</p>
        </div>
        <div className="rounded-2xl bg-amber-50 px-4 py-3 text-amber-800">
          <PaymentsOutlinedIcon style={{ fontSize: 18 }} />
          <p className="mt-1 font-bold">Wallet, UPI, or monthly billing</p>
        </div>
      </div>

      {startOpen && (
        <StartSubscriptionModal
          products={subscription_products}
          addresses={user_addresses}
          frequencies={configFrequencies}
          deliverySlots={configDeliverySlots}
          paymentMethods={configPaymentMethods}
          initialProductId={initialProductId}
          initialVariantId={initialVariantId}
          loading={subscription_action_loading || subscription_products_loading || subscription_config_loading}

          onSubmit={handleCreate}
          onClose={() => {
            setStartOpen(false);
            setSearchParams({});
            setModalProductId("");
            setModalVariantId("");
          }}
        />
      )}

      {pauseTarget && (
        <PauseModal
          subscription={pauseTarget}
          loading={subscription_action_loading}
          onSubmit={handlePause}
          onClose={() => setPauseTarget(null)}
        />
      )}

      {resumeTarget && (
        <ResumeModal
          subscription={resumeTarget}
          loading={subscription_action_loading}
          onSubmit={handleResume}
          onClose={() => setResumeTarget(null)}
        />
      )}

      {editTarget && (
        <EditSubscriptionModal
          subscription={editTarget}
          loading={subscription_action_loading}
          onSubmit={handleEdit}
          onClose={() => setEditTarget(null)}
        />
      )}

      {detailOpen && (
        <DetailModal
          detail={subscription_detail}
          deliveries={subscription_deliveries}
          detailLoading={subscription_detail_loading}
          deliveriesLoading={subscription_deliveries_loading}
          onLoadDeliveries={fetchSubscriptionDeliveries}
          onClose={() => setDetailOpen(false)}
        />
      )}

      {/* ── UPI AutoPay Mandate Result Overlay ─────────────────────────────── */}
      {mandateStatus !== null && (
        <MandateResultScreen
          status={mandateStatus}
          subscription={mandateSubscription}
          paymentLink={mandatePaymentLink}
          onViewSubscriptions={handleMandateDismiss}
          onCheckStatus={handleCheckMandateStatus}
          onRetry={handleMandateRetry}
          isPolling={mandatePolling}
        />
      )}
    </div>
  );
};

export default SubscriptionsSection;
